import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import createProvider from '@/shared/api/shipping/providers/createProvider';
import updateProvider from '@/shared/api/shipping/providers/updateProvider';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import CreateShippingProvider from '@/shared/types/shipping/createShippingProvider.type';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<ShippingProvider, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useManageShippingProviderDialog = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['shipping']);

  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const handleCreateShippingProvider = async (
    provider: CreateShippingProvider,
  ) => {
    onUpdateProgressDialog({
      title: t('shipping:addingShippingProvider'),
      open: true,
    });

    const response = await createProvider({
      body: provider,
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateDialogState(DialogName.ManageShippingProvider, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:shippingProviderAddedSuccessfully'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.ManageShippingProvider, {
        errors: [response.msg],
      });
    }
  };

  const handleUpdateShippingProvider = async (
    provider: CreateShippingProvider,
    providerId?: string,
  ) => {
    onUpdateProgressDialog({
      title: t('shipping:updatingShippingProvider'),
      open: true,
    });

    const response = await updateProvider({
      providerId: providerId || '',
      body: provider,
      onUnauthorized: redirectLogin,
    });

    onUpdateProgressDialog({ open: false });

    if (response.success) {
      setRefreshTableIndex((prev) => prev + 1);
      onUpdateDialogState(DialogName.ManageShippingProvider, {
        open: false,
      });
      onUpdateNotificationBar({
        message: t('shipping:shippingProviderUpdatedSuccessfully'),
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.ManageShippingProvider, {
        errors: [response.msg],
      });
    }
  };

  return {
    refreshTableIndex,
    handleCreateShippingProvider,
    handleUpdateShippingProvider,
  };
};

export default useManageShippingProviderDialog;
